import {
    ITextBetLineNumberConfig,
    TextBetLineNumber
} from "@nolimitcity/slot-game/bin/game/betline/number/TextBetLineNumber";
import {TombOfNefertitiGameConfig} from "../../TombOfNefertitiGameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {TombOfNefertitiGameAssets} from "../../TombOfNefertitiGameAssets";
import {TweenLite} from "gsap";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {TombOfNefertitiAnimatedWinlineController} from "../../winpresentation/animatedwinlines/TombOfNefertitiAnimatedWinlineController";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";

export function isNumber(value:any):value is number {
    return typeof value === "number" && value === value;
}

export class TombOfNefertitiTextBetlineNumber extends TextBetLineNumber {

    private readonly _TONHorizontalPadding: [number, number];
    private readonly _TONVerticalPadding: [number, number];

    private _normal: PIXI.Sprite;
    private _normalAlphaTween: TweenLite;
    private _normalScaleTween: TweenLite;

    private _win: PIXI.Sprite;
    private _winAlphaTween: TweenLite;
    private _winScaleTween: TweenLite;

    /**
     * @constructs
     *
     * @param index
     * @param config
     */
    constructor(index: number, config: ITextBetLineNumberConfig) {
        super(index, config);

        if (isNumber(config.horizontalPadding)) {
            this._TONHorizontalPadding = [config.horizontalPadding, config.horizontalPadding];
        } else {
            this._TONHorizontalPadding = config.horizontalPadding;
        }

        if (isNumber(config.verticalPadding)) {
            this._TONVerticalPadding = [config.verticalPadding, config.verticalPadding];
        } else {
            this._TONVerticalPadding = config.verticalPadding;
        }

        EventHandler.addEventListener(this, TombOfNefertitiAnimatedWinlineController.PLAY_INDIVIDUAL_WINLINE, (event: GameEvent) => this.showIndividualNumber(event.params[0]));
    }

    // Hardcoded default even number betLines. positionIndex starts from 0.
    /**
     * @description It sets the positions of the betline numbers.
     *
     * @param betLinesNum
     * @param positionIndex
     */
    public setPosition(betLinesNum: number, positionIndex: number): number[] {
        const stageWidth: number = TombOfNefertitiGameConfig.instance.GAME_WIDTH,
            stageHeight: number = TombOfNefertitiGameConfig.instance.GAME_HEIGHT,
            numLineInOneSide: number = betLinesNum / 2,
            numberLeftPositionX: number = this._TONHorizontalPadding[0],
            numberRightPositionX: number = stageWidth - this._TONHorizontalPadding[1],
            numberVerticalSpace: number = (stageHeight - this._TONVerticalPadding[0] - this._TONVerticalPadding[1]) / (numLineInOneSide - 1),
            side: number = Math.floor(positionIndex / numLineInOneSide),
            x: number = side === 0 ? numberLeftPositionX : numberRightPositionX,
            y: number = Math.round(positionIndex % numLineInOneSide * numberVerticalSpace + this._TONVerticalPadding[0]),
            offsetXFSBetlineNumbers: number = 12,
            offsetXRightSide: number = 5,
            offsetY: number = 2.5;

        // overridden here
        if (positionIndex % 2 !== 0) {
            this.x = side === 0 ? x : x + offsetXRightSide;
        } else {
            this.x = side === 0 ? x + offsetXFSBetlineNumbers : (x + offsetXRightSide) - offsetXFSBetlineNumbers;
        }

        this.y = y + offsetY;

        return [x, y, side];
    }

    /**
     * @description This is overridden to apply background image to betline numbers.
     *
     * @param config
     */
    protected draw(config: ITextBetLineNumberConfig): void {
        super.draw(config);

        this.addChild(this.addBackground());
        this.addChild(this.addGlow());
        this.addChild(this.addNumber());
    }

    protected showMouseOver(): void {
        super.showMouseOver();

        this.killAll();

        this._normalAlphaTween = TweenLite.to(this._normal, 0.1, {alpha: 0});
        this._winAlphaTween = TweenLite.to(this._win, 0.1, {alpha: 1});
    }

    protected showMouseOut(): void {
        super.showMouseOut();

        this.killAll();

        this._normalAlphaTween = TweenLite.to(this._normal, 0.1, {alpha: 1});
        this._winAlphaTween = TweenLite.to(this._win, 0.1, {alpha: 0});
    }

    protected showWin(): void {
        super.showWin();

        this.killAll();

        this._normalAlphaTween = TweenLite.to(this._normal, 0.1, {alpha: 0});
        this._winAlphaTween = TweenLite.to(this._win, 0.1, {alpha: 1});
        this._normalScaleTween = TweenLite.to(this._normal.scale, 0.17, {
            x: 1,
            y: 1
        });
        this._winScaleTween = TweenLite.to(this._win.scale, 0.17, {
            x: 1,
            y: 1
        });
    }

    protected showNormal(): void {
        super.showNormal();

        this.killAll();

        this._normalAlphaTween = TweenLite.to(this._normal, 0.33, {alpha: 1});
        this._winAlphaTween = TweenLite.to(this._win, 0.33, {alpha: 0});
        this._normalScaleTween = TweenLite.to(this._normal.scale, 0.33, {x: 1, y: 1});
        this._winScaleTween = TweenLite.to(this._win.scale, 0.33, {x: 1, y: 1});
    }

    /**
     * @description This method adds the background to the betline numbers using relevant texture.
     */
    private addBackground(): PIXI.DisplayObject {
        let bg: PIXI.Sprite,
            textureName: string;

        const posOffset: number = 2.5;

        // free spin betline numbers
        if (this.index > 20) {
            textureName = TombOfNefertitiGameAssets.BONUS_GAME_BETLINE_NUMBERS_BG;
        } else {
            textureName = TombOfNefertitiGameAssets.MAIN_GAME_BETLINE_NUMBERS_BG;
        }

        bg = new PIXI.Sprite(GameResources.getTextures(textureName)[0]);
        bg.anchor.set(0.5);

        bg.x -= posOffset;
        bg.y -= posOffset;

        return bg;
    }

    private addGlow(): PIXI.DisplayObject {
        const name: string = "betlineNumbers/Increased/Increased_";

        this._win = this.createImageItem(name);

        return this._win;
    }

    private addNumber(): PIXI.DisplayObject {
        const name: string = "betlineNumbers/Normal/normal_";

        this._normal = this.createImageItem(name);

        return this._normal;
    }

    private createImageItem(name: string): PIXI.Sprite {
        const offset: number = 2.5;
        let item: PIXI.Sprite;

        item = new PIXI.Sprite(GameResources.getTextures(name + this.index)[0]);
        item.anchor.set(0.5);
        item.x -= offset;
        item.y -= offset;

        return item;
    }

    private killAll(): void {
        if (this._normalAlphaTween && this._normalAlphaTween.isActive()) {
            this._normalAlphaTween.pause();
            this._normalAlphaTween.kill();
        }

        if (this._normalScaleTween && this._normalScaleTween.isActive()) {
            this._normalScaleTween.pause();
            this._normalScaleTween.kill();
        }

        if (this._winAlphaTween && this._winAlphaTween.isActive()) {
            this._winAlphaTween.pause();
            this._winAlphaTween.kill();
        }

        if (this._winScaleTween && this._winScaleTween.isActive()) {
            this._winScaleTween.pause();
            this._winScaleTween.kill();
        }
    }

    private showIndividualNumber(index: number): void {
        if (index + 1 !== this.index) {
            return;
        }

        this.showWin();
    }
}